import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Empty, Result, Space, Spin, Table, Tag, Typography } from 'antd';
import { CloudOutlined, ExportOutlined, PlusOutlined, ReloadOutlined, RocketOutlined } from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import PageHeader from '../../../shared/components/PageHeader';
import AddonPanel from '../../../shared/components/AddonPanel';
import diskService, { openDisk } from '../../services/diskService';
import PlanChangeModal from './PlanChangeModal';
import { ModeTag, StatusTag, UsageBar, fmtBytes, storageOf, toastErr, trafficOf } from './common';

const { Text } = Typography;

/** disk.yeap.cloud 登录页「用控制台账号登录」→ /disk?sso=1：直接换票跳回网盘 */
const SsoRedirect = ({ accountId }) => {
    const { t } = useTranslation();
    const [, setSp] = useSearchParams();
    const [err, setErr] = useState(null);
    const started = useRef(false);

    const go = useCallback(async () => {
        setErr(null);
        try {
            const r = await diskService.sso(accountId);
            if (!r?.data?.url) throw new Error(t('Redirect failed'));
            window.location.href = r.data.url;
        } catch (e) {
            setErr(e?.error || e?.message || t('Redirect failed'));
        }
    }, [accountId, t]);

    useEffect(() => {
        if (started.current) return;
        started.current = true;
        go();
    }, [go]);

    if (err) {
        return (
            <Result status="warning" title={err}
                extra={<Space><Button type="primary" onClick={go}>{t('Retry')}</Button><Button onClick={() => setSp({}, { replace: true })}>{t('Back to console')}</Button></Space>} />
        );
    }
    return <div style={{ padding: 80, textAlign: 'center' }}><Spin size="large" tip={t('Opening Drive…')}><div style={{ height: 40 }} /></Spin></div>;
};

const DiskOverview = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [planOpen, setPlanOpen] = useState(false);

    const sso = sp.get('sso') === '1';

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await diskService.overview();
            setData(r?.data || null);
        } catch (e) {
            toastErr(e, t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { if (!sso) load(); }, [load, sso]);

    if (sso) return <SsoRedirect accountId={sp.get('account_id') || undefined} />;

    const personal = data?.personal || {};
    const plan = personal.plan || {};
    const st = storageOf(personal.usage);
    const tr = trafficOf(personal.usage);
    const orgs = Array.isArray(data?.orgs) ? data.orgs : [];
    const memberships = Array.isArray(data?.memberships) ? data.memberships : [];
    const open = (accountId) => openDisk(accountId, (e) => toastErr(e, t('Redirect failed')));
    const openOrg = (o) => openDisk(async () => {
        // 组织里我的身份（owner 账号）；概览里没带时查一次详情
        if (o.account_id || o.owner_account_id) return o.account_id || o.owner_account_id;
        const r = await diskService.getOrg(o.id);
        const d = r?.data || {};
        const id = d.account_id || d.org?.account_id || d.org?.owner_account_id;
        if (!id) throw new Error(t('Redirect failed'));
        return id;
    }, (e) => toastErr(e, t('Redirect failed')));

    const orgCols = [
        { title: t('Name'), dataIndex: 'name', render: (v, o) => <Link to={userPath(`/disk/orgs/${o.id}`)}>{v}</Link> },
        { title: t('Mode'), dataIndex: 'quota_mode', width: 110, render: (v, o) => (o.kind === 'mail' ? <Tag color="purple" style={{ margin: 0 }}>{t('Mail org')}</Tag> : <ModeTag mode={v} t={t} />) },
        {
            title: t('Used'), key: 'u', width: 260,
            render: (_, o) => { const s = storageOf(o.usage); return <UsageBar used={s.used} limit={s.limit} t={t} />; },
        },
        {
            title: t('Members'), key: 's', width: 90,
            render: (_, o) => (o.usage?.seats_used !== undefined ? `${o.usage.seats_used}${o.usage.seat_limit ? ` / ${o.usage.seat_limit}` : ''}` : '-'),
        },
        { title: t('Status'), dataIndex: 'status', width: 90, render: (v) => <StatusTag status={v} t={t} /> },
        {
            title: '', key: 'a', width: 180, align: 'right',
            render: (_, o) => (
                <Space size={4}>
                    <Button size="small" onClick={() => navigate(userPath(`/disk/orgs/${o.id}`))}>{t('Manage')}</Button>
                    <Button size="small" icon={<ExportOutlined />} disabled={o.status !== 'active'}
                        onClick={() => openOrg(o)}>{t('Open Drive')}</Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <PageHeader title={t('Cloud Drive')} icon={<CloudOutlined />}
                extra={<Space>
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                    <Button type="primary" icon={<ExportOutlined />} onClick={() => open()}>{t('Open Drive')}</Button>
                </Space>} />

            <Spin spinning={loading && !data}>
                <Card title={t('Personal space')} style={{ marginBottom: 16 }}
                    extra={plan.is_free || !plan.name
                        ? <Tag>{t('Free {{size}}', { size: fmtBytes(st.limit) })}</Tag>
                        : <Tag color="blue">{plan.name}</Tag>}>
                    {personal.usage?.purge_at && (
                        <Alert type="error" showIcon style={{ marginBottom: 12 }}
                            message={t('Your drive files will be deleted after {{date}}. Renew or upgrade to keep them', { date: new Date(personal.usage.purge_at).toLocaleDateString() })} />
                    )}
                    <UsageBar label={t('Storage')} used={st.used} limit={st.limit} t={t} />
                    <UsageBar label={t('Direct-link traffic this month')} used={tr.used} limit={tr.limit} t={t} />
                    <Space style={{ marginTop: 8 }} wrap>
                        <Button type="primary" icon={<ExportOutlined />} onClick={() => open(personal.account_id)}>{t('Open Drive')}</Button>
                        {personal.subscription_id
                            ? <Button icon={<RocketOutlined />} onClick={() => setPlanOpen(true)}>{t('Upgrade / change plan')}</Button>
                            : <Button icon={<RocketOutlined />} onClick={() => navigate(userPath('/provision/disk?mode=personal'))}>{t('Buy storage')}</Button>}
                    </Space>
                </Card>

                {personal.subscription_id && <AddonPanel subscriptionId={personal.subscription_id} onChanged={load} />}

                <Card title={t('Organization')}
                    extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(userPath('/provision/disk?mode=org'))}>{t('Create organization')}</Button>}>
                    {orgs.length === 0
                        ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text type="secondary">{t('Set up a shared drive for your team')}</Text>} />
                        : <Table rowKey="id" size="middle" columns={orgCols} dataSource={orgs} pagination={false} scroll={{ x: 760 }} />}
                </Card>

                {memberships.length > 0 && (
                    <Card title={t('Orgs I joined')} style={{ marginTop: 16 }}>
                        <Table rowKey="account_id" size="middle" pagination={false} dataSource={memberships} columns={[
                            { title: t('Name'), dataIndex: 'org_name' },
                            { title: t('Role'), dataIndex: 'role', width: 110, render: (v) => <Tag style={{ margin: 0 }}>{v === 'admin' ? t('Admin') : t('Members')}</Tag> },
                            { title: t('Status'), dataIndex: 'status', width: 90, render: (v) => <StatusTag status={v} t={t} /> },
                            {
                                title: '', key: 'a', width: 130, align: 'right',
                                render: (_, m) => <Button size="small" icon={<ExportOutlined />} disabled={m.status !== 'active'}
                                    onClick={() => open(m.account_id)}>{t('Open Drive')}</Button>,
                            },
                        ]} />
                    </Card>
                )}
            </Spin>

            <PlanChangeModal open={planOpen} subscriptionId={personal.subscription_id} onClose={() => setPlanOpen(false)} />
        </div>
    );
};

export default DiskOverview;
